import { z } from 'zod';

import { SchainType, stringifyRewriteValue, type RewriteSet } from '../../supply/lists/model';
import { rewriteFields } from '../../supply/lists/rewriteFields';

export enum IntegrationRewriteSection {
  App = 'APP',
  Site = 'SITE',
  User = 'USER',
  Video = 'VIDEO',
  Device = 'DEVICE',
  Content = 'CONTENT',
  Producer = 'PRODUCER',
  Network = 'NETWORK',
  Channel = 'CHANNEL',
  Regulations = 'REGULATIONS',
  Impression = 'IMPRESSION',
  Publisher = 'PUBLISHER',
  Source = 'SOURCE',
}

export enum IntegrationAttachedListType {
  Rewrite = 'REWRITE',
  Schain = 'SCHAIN',
}

export const integrationRewriteSetSchema = z.object({
  field: z.string(),
  value: z.string(),
  overwrite: z.boolean(),
  nullify: z.boolean(),
});

export const attachedRewriteListSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
});

export const attachedSchainListSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  type: z.enum(SchainType).optional(),
});

export type IntegrationRewriteSet = z.infer<typeof integrationRewriteSetSchema>;
export type AttachedRewriteList = z.infer<typeof attachedRewriteListSchema>;
export type AttachedSchainList = z.infer<typeof attachedSchainListSchema>;

export interface IntegrationRewriteSectionConfig {
  id: IntegrationRewriteSection;
  fields: readonly string[];
}

const sectionOrder = [
  IntegrationRewriteSection.App,
  IntegrationRewriteSection.Site,
  IntegrationRewriteSection.User,
  IntegrationRewriteSection.Video,
  IntegrationRewriteSection.Device,
  IntegrationRewriteSection.Content,
  IntegrationRewriteSection.Producer,
  IntegrationRewriteSection.Network,
  IntegrationRewriteSection.Channel,
  IntegrationRewriteSection.Regulations,
  IntegrationRewriteSection.Impression,
  IntegrationRewriteSection.Publisher,
  IntegrationRewriteSection.Source,
] as const;

export const integrationRewriteSections: readonly IntegrationRewriteSectionConfig[] =
  sectionOrder.map((id) => ({
    id,
    fields: rewriteFields.filter((field) => rewriteSectionForField(field) === id),
  }));

export function createIntegrationRewriteSets(raw: string | null | undefined) {
  const existingSets = extractRewriteSets(parseRewriteJson(raw));
  const setsByField = new Map(
    existingSets
      .filter((set) => typeof set.field === 'string' && set.field)
      .map((set) => [set.field, normalizeSet(set)]),
  );
  const knownFields = new Set<string>(rewriteFields);
  return [
    ...rewriteFields.map((field) => setsByField.get(field) ?? emptyIntegrationRewriteSet(field)),
    ...existingSets
      .filter((set) => typeof set.field === 'string' && !knownFields.has(set.field))
      .map(normalizeSet),
  ];
}

export function serializeIntegrationRewriteSets(sets: readonly IntegrationRewriteSet[]) {
  return JSON.stringify({
    condGroups: [],
    sets: sets.map((set): RewriteSet => {
      const value = stringifyRewriteValue(set.value).trim();
      const hasValue = hasRewriteValue(set.value);
      return {
        field: set.field,
        value,
        overwrite: hasValue ? set.overwrite : false,
        nullify: hasValue ? false : set.nullify,
      };
    }),
  });
}

export function integrationRewriteSectionActiveCount(
  section: IntegrationRewriteSectionConfig,
  sets: readonly IntegrationRewriteSet[],
) {
  const sectionFields = new Set(section.fields);
  return sets.filter(
    (set) =>
      sectionFields.has(set.field) && (hasRewriteValue(set.value) || set.overwrite || set.nullify),
  ).length;
}

export function hasRewriteValue(value: string) {
  return value.trim().length > 0;
}

export function emptyIntegrationRewriteSet(field: string): IntegrationRewriteSet {
  return { field, value: '', overwrite: false, nullify: false };
}

function parseRewriteJson(raw: string | null | undefined): unknown {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

function extractRewriteSets(payload: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(payload)) {
    if (payload.every(isRewriteSetRecord)) return payload;
    return payload.flatMap((rule) =>
      isRecord(rule) && Array.isArray(rule.sets) ? rule.sets.filter(isRecord) : [],
    );
  }
  if (!isRecord(payload)) return [];
  if (Array.isArray(payload.sets)) return payload.sets.filter(isRecord);
  if (Array.isArray(payload.rewrites)) return payload.rewrites.filter(isRecord);
  return [];
}

function normalizeSet(set: Record<string, unknown>): IntegrationRewriteSet {
  const value = stringifyRewriteValue(set.value);
  const hasValue = hasRewriteValue(value);
  return {
    field: typeof set.field === 'string' ? set.field : '',
    value,
    overwrite: hasValue && Boolean(set.overwrite),
    nullify: !hasValue && Boolean(set.nullify),
  };
}

function rewriteSectionForField(field: string) {
  if (field.startsWith('app.')) return IntegrationRewriteSection.App;
  if (field.startsWith('site.')) return IntegrationRewriteSection.Site;
  if (field.startsWith('user.')) return IntegrationRewriteSection.User;
  if (field.startsWith('imp.video.')) return IntegrationRewriteSection.Video;
  if (field.startsWith('device.')) return IntegrationRewriteSection.Device;
  if (field.startsWith('content.')) return IntegrationRewriteSection.Content;
  if (field.startsWith('producer.')) return IntegrationRewriteSection.Producer;
  if (field.startsWith('network.')) return IntegrationRewriteSection.Network;
  if (field.startsWith('channel.')) return IntegrationRewriteSection.Channel;
  if (field.startsWith('regs.')) return IntegrationRewriteSection.Regulations;
  if (field.startsWith('imp.') || field.startsWith('imps.'))
    return IntegrationRewriteSection.Impression;
  if (field.startsWith('publisher.')) return IntegrationRewriteSection.Publisher;
  return IntegrationRewriteSection.Source;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isRewriteSetRecord(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && typeof value.field === 'string';
}
